/**
 * AoaInspectorPanel — the right-side panel.
 *
 * One slot at a time:
 *   - a ProposalCard / FileManagerProposalCard for the active proposal
 *   - (Phase 2) a SimulationPanelView for the active eval conversation
 *
 * Header shows:
 *   - panel title (matches the active item)
 *   - "N waiting" chip when proposals are queued (click to peek the queue)
 *   - collapse button
 *
 * Behaviour:
 *   - When `active` is null AND no queue: shows a polite empty state
 *     ("Cards from the agent will appear here").
 *   - Esc closes the panel ONLY if the user isn't inside the card's
 *     text fields (so we don't swallow normal text-editing keys).
 *   - Width is owned by the parent layout via grid template; this
 *     component never sets its own width — that lets the drag handle
 *     in the layout work without fighting React state.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { useInspector, type InspectorItem } from './InspectorContext';
import { simulationOnScreen as isSimulationOnScreen } from '../fileActivation';
import { ProposalCard } from './ProposalCard';
import { SimulationPanelView } from './SimulationPanelView';
import ArtifactPanel from '@modules/conversations/components/artifacts/ArtifactPanel';
import ArtifactErrorBoundary from '@modules/conversations/components/artifacts/ArtifactErrorBoundary';
import { FilePreviewContent } from '@components/common/FilePreviewContent';
import { artifactService } from '@modules/conversations/services/artifactService';
import type { AoAProposal, ProposalResolution } from '../types';
import { cardTitleOf, type Translate } from '../proposalLabels';
import './AoaInspectorPanel.css';
import { mirrorPlacement } from '@/utils/rtl';

function titleFor(t: Translate, item: InspectorItem): string {
  if (item.kind === 'proposal') {
    // The card carries its own rich, specific title (and it stays pinned while
    // scrolling), so the panel chrome shows a stable neutral label instead of
    // repeating it — no more "Editing an agent" twice, one above the other.
    return t('metaAgent.inspector.title');
  }
  if (item.kind === 'simulation') {
    return t('metaAgent.inspector.simulationTitle', { name: item.target_agent_name });
  }
  if (item.kind === 'artifact') {
    return t('metaAgent.inspector.artifactTitle');
  }
  return t('metaAgent.inspector.title');
}

export const AoaInspectorPanel: React.FC = () => {
  const { t, direction } = useLanguage();
  const {
    active,
    panelTab,
    setPanelTab,
    proposalQueue,
    collapsed,
    toggleCollapsed,
    closeActive,
    updateProposal,
    setActiveProposal,
    proposalResolvedListenerRef,
    artifacts,
    libraryPreview,
    setLibraryPreview,
  } = useInspector();

  // Local UI: queue peek-flyout open/closed.
  const [queueOpen, setQueueOpen] = useState(false);

  // Close the queue peek whenever the active item changes or the queue
  // empties — keeps the UI tidy.
  useEffect(() => {
    setQueueOpen(false);
  }, [active?.kind, proposalQueue.length]);

  // Esc closes the panel — BUT not when the focus is inside a form
  // field on the card itself (otherwise the user editing a textarea
  // can't press Esc to cancel a multiline shortcut).
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) {
        return;
      }
      closeActive();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, closeActive]);

  // Handler the ProposalCard fires when the user resolves. We:
  //   1. push the updated proposal into inspector state so the chat
  //      markers re-render with the new status
  //   2. notify the chat panel's auto-continue listener (so accepting a
  //      proposal from the side panel still resumes the agent's flow)
  //   3. if the proposal actually closed (status !== pending), pop the
  //      next queued card after a brief "applied" beat
  const onProposalResolved = useCallback(
    (resolution: ProposalResolution, updated: AoAProposal) => {
      updateProposal(updated);
      proposalResolvedListenerRef.current?.(resolution, updated);
      if (updated.status !== 'pending') {
        // 350ms feels confident without being sluggish — long enough for
        // the user to read "applied" before the next card slides in.
        const handle = window.setTimeout(() => closeActive(), 350);
        return () => window.clearTimeout(handle);
      }
      return undefined;
    },
    [updateProposal, closeActive, proposalResolvedListenerRef],
  );

  // The Cards tab's item names the panel; over the Files tab it would name
  // something that is not on screen.
  const title = active && panelTab === 'cards' ? titleFor(t, active) : t('metaAgent.inspector.title');
  const queueLen = proposalQueue.length;
  // Tab badges: how many things live behind each tab. Cards = the active
  // pending card plus the queue; Files = every artifact this conversation
  // produced. Counts, not alarms — they answer "is there anything in there?".
  const cardCount = queueLen
    + (active?.kind === 'proposal' && active.proposal.status === 'pending' ? 1 : 0);
  const fileCount = artifacts.order.length;

  // Collapsed = a thin vertical rail with just the expand button and
  // (if relevant) a pending-cards badge. Keeps the panel discoverable
  // when the user has tucked it away.
  if (collapsed) {
    return (
      <aside className="aoa-inspector aoa-inspector--collapsed" aria-label={t('metaAgent.inspector.inspectorPanel')}>
        <Tooltip title={t('metaAgent.inspector.openInspector')} placement={mirrorPlacement(direction, 'left')} overlayClassName="aoa-tooltip">
          <button
            type="button"
            className="aoa-inspector__rail-btn"
            onClick={toggleCollapsed}
            aria-label={t('metaAgent.inspector.openInspectorPanel')}
            data-testid="aoa-inspector-expand"
          >
            <svg className="rtl-flip" width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 6l-6 6 6 6" />
            </svg>
            {(active || queueLen > 0) && (
              <span className="aoa-inspector__rail-badge" aria-hidden="true">
                {queueLen + (active ? 1 : 0)}
              </span>
            )}
          </button>
        </Tooltip>
      </aside>
    );
  }

  // For simulations the slot renders its own header (agent avatar +
  // name + status), so we suppress the inspector's title row to avoid
  // a duplicated header. The queue chip + collapse button still need a
  // home — SimulationPanelView reads them off the inspector context
  // and renders them in its own header's right side. Only while it is ON
  // SCREEN: over the Files tab (a file the meta-agent produced after a
  // simulation) the panel needs its own title row back. The tabs stay in both
  // cases — with them hidden a simulation left no way to the files at all.
  const simulationOnScreen = isSimulationOnScreen(active, panelTab);

  return (
    <aside className="aoa-inspector" aria-label={t('metaAgent.inspector.inspectorPanel')}>
      {!simulationOnScreen && (
        <header className="aoa-inspector__header">
          <div className="aoa-inspector__title-row">
            <span dir="auto" className="aoa-inspector__title">{title}</span>
            {queueLen > 0 && (
              <button
                type="button"
                className={`aoa-inspector__queue-chip ${queueOpen ? 'is-open' : ''}`}
                onClick={() => setQueueOpen((o) => !o)}
                aria-label={t('counts.moreCardsWaiting', { count: queueLen })}
                data-testid="aoa-inspector-queue-chip"
              >
                <span className="aoa-inspector__queue-dot" aria-hidden="true" />
                {queueLen} waiting
              </button>
            )}
          </div>
          <div className="aoa-inspector__header-actions">
            <Tooltip title={t('metaAgent.inspector.collapsePanel')} placement="bottom" overlayClassName="aoa-tooltip">
              <button
                type="button"
                className="aoa-inspector__icon-btn"
                onClick={toggleCollapsed}
                aria-label={t('metaAgent.inspector.collapsePanel')}
                data-testid="aoa-inspector-collapse"
              >
                <svg className="rtl-flip" width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </Tooltip>
          </div>
        </header>
      )}

      {/* Cards | Files. Two SURFACES, not one slot they take turns owning:
          a preview and a question are different kinds of thing, and making
          them compete meant one always hid the other. Switching is free and
          decides nothing — the pending card is exactly where you left it. */}
      <div className="aoa-inspector__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={panelTab === 'cards'}
          className={`aoa-inspector__tab ${panelTab === 'cards' ? 'is-active' : ''}`}
          onClick={() => setPanelTab('cards')}
          data-testid="aoa-inspector-tab-cards"
        >
          {t('metaAgent.inspector.tabCards')}
          {cardCount > 0 && (
            <span className="aoa-inspector__tab-count">{cardCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={panelTab === 'files'}
          className={`aoa-inspector__tab ${panelTab === 'files' ? 'is-active' : ''}`}
          onClick={() => setPanelTab('files')}
          data-testid="aoa-inspector-tab-files"
        >
          {t('metaAgent.inspector.tabFiles')}
          {fileCount > 0 && (
            <span className="aoa-inspector__tab-count">{fileCount}</span>
          )}
        </button>
      </div>

      {queueOpen && queueLen > 0 && (
        <div className="aoa-inspector__queue-peek">
          <div className="aoa-inspector__queue-peek-title">{t('metaAgent.inspector.upNext')}</div>
          <ul className="aoa-inspector__queue-list">
            {proposalQueue.map((p) => (
              <li key={p.proposal_id}>
                <button
                  type="button"
                  className="aoa-inspector__queue-item"
                  onClick={() => { setActiveProposal(p); setQueueOpen(false); }}
                >
                  <span className="aoa-inspector__queue-item-tool" dir="auto">
                    {cardTitleOf(t, p.tool_name, (p.proposed_payload || null) as Record<string, unknown> | null)}
                  </span>
                  {p.rationale && (
                    <span className="aoa-inspector__queue-item-rationale" dir="auto">
                      {p.rationale.length > 60 ? p.rationale.slice(0, 60) + '…' : p.rationale}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* A proposal owns the panel height: the card scrolls its own content so
          Accept / Reject stay pinned in view. Everything else keeps the
          panel-level scroll. */}
      <div
        className={
          'aoa-inspector__body'
          + (panelTab === 'cards' && active?.kind === 'proposal'
            ? ' aoa-inspector__body--proposal' : '')
        }
      >
        {panelTab === 'files' ? (
          libraryPreview ? (
            <LibraryFilePreviewView
              preview={libraryPreview}
              onClose={() => setLibraryPreview(null)}
            />
          ) : fileCount > 0 ? (
            <InspectorArtifactView />
          ) : (
            <div className="aoa-inspector__empty">
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                   className="aoa-inspector__empty-icon">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              <div className="aoa-inspector__empty-title">
                {t('metaAgent.inspector.noFilesTitle')}
              </div>
              <div className="aoa-inspector__empty-sub">
                {t('metaAgent.inspector.noFilesSub')}
              </div>
            </div>
          )
        ) : active ? (
          <InspectorSlot item={active} onProposalResolved={onProposalResolved} />
        ) : (
          <div className="aoa-inspector__empty">
            <svg width="42" height="42" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                 className="aoa-inspector__empty-icon">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M3 10h18" />
              <path d="M9 14h6" />
            </svg>
            <div className="aoa-inspector__empty-title">{t('metaAgent.inspector.noActiveCard')}</div>
            <div className="aoa-inspector__empty-sub">{t('metaAgent.inspector.emptySub')}</div>
          </div>
        )}
      </div>
    </aside>
  );
};

// ---------------------------------------------------------------------------
// Slot renderer — dispatches on the discriminated union.
/** A stored Library file the agent opened for the user — read-only preview
 *  plus download. Sits above the artifact list on the Files tab; closing it
 *  returns to the artifacts. Access was already enforced server-side by the
 *  tool that raised it; the download reuses the by-id endpoint every other
 *  surface uses. */
const LibraryFilePreviewView: React.FC<{
  preview: { file_id: string; file_name: string; file_type?: string };
  onClose: () => void;
}> = ({ preview, onClose }) => {
  const { t } = useLanguage();
  return (
    <div className="aoa-inspector__library-preview">
      <div className="aoa-inspector__library-preview-bar">
        <button
          type="button"
          className="aoa-inspector__icon-btn"
          onClick={onClose}
          aria-label={t('metaAgent.inspector.backToFiles')}
          data-testid="aoa-library-preview-back"
        >
          <svg className="rtl-flip" width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        <span className="aoa-inspector__library-preview-name" title={preview.file_name} dir="auto">
          {preview.file_name}
        </span>
        <button
          type="button"
          className="aoa-inspector__icon-btn"
          onClick={() => { void artifactService.download(preview.file_id, preview.file_name); }}
          aria-label={t('metaAgent.inspector.downloadFile')}
          data-testid="aoa-library-preview-download"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5M12 15V3" />
          </svg>
        </button>
      </div>
      <div className="aoa-inspector__library-preview-body">
        <FilePreviewContent
          fileId={preview.file_id}
          fileName={preview.file_name}
          fileType={preview.file_type}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const InspectorSlot: React.FC<{
  item: InspectorItem;
  onProposalResolved: (
    resolution: ProposalResolution,
    updated: AoAProposal,
  ) => void | (() => void);
}> = ({ item, onProposalResolved }) => {
  if (item.kind === 'proposal') {
    return (
      <ProposalCard
        // Key by proposal id: when one proposal replaces another in the
        // active slot (revision replacement, marker click, queue advance),
        // an unkeyed card is REUSED and its mount-time state survives — the
        // Edit textarea then opens with the PREVIOUS proposal's text
        // (reported 2026-08-04: "the edit button contains the old version
        // of the system prompt"). A key forces a fresh mount per proposal.
        key={item.proposal.proposal_id}
        proposal={item.proposal}
        onResolved={(res, updated) => { onProposalResolved(res, updated); }}
      />
    );
  }
  if (item.kind === 'simulation') {
    // Key by TARGET CHATBOT ID, not eval_conversation_id.
    //
    // Why: on a brand-new eval the panel opens with a placeholder
    // `pending-<chatbotId>` id while the AoA's tool is in flight.
    // When the tool result lands, AoAChatPanel swaps the id to the
    // real UUID. If we keyed on eval_id, that swap would unmount +
    // remount this component, wiping local message state — so both
    // the user bubble AND the assistant bubble would render fresh on
    // the new mount (the "renders the whole turn together" visual
    // the user flagged 2026-06-12). Keying on target_chatbot_id
    // ABSORBS the placeholder→real-id transition: same React
    // component instance, props update, the optimistic user bubble
    // stays mounted, only the assistant bubble appears as new.
    //
    // The persona enforces "one continuous eval per target", so
    // target_chatbot_id is the stable identity here. Switching to a
    // different target chatbot still remounts (different key).
    // "Start a new test conversation" against the SAME target just
    // means evalConversationId changes via props — the load effect
    // refetches the new conversation, no remount needed.
    return (
      <SimulationPanelView
        key={item.target_chatbot_id ?? item.eval_conversation_id}
        evalConversationId={item.eval_conversation_id}
        targetChatbotId={item.target_chatbot_id}
        targetAgentName={item.target_agent_name}
        scrollToMessageId={item.scrollToMessageId ?? null}
      />
    );
  }
  if (item.kind === 'artifact') {
    return <InspectorArtifactView />;
  }
  return null;
};

/** The chat's own artifact panel, hosted in the inspector's body.
 *
 *  Deliberately the SAME component, not a copy: a document the meta-agent
 *  wrote is the same kind of object as one any other agent wrote, so it gets
 *  the same preview, the same version dropdown and the same download. Resizing
 *  is the inspector's job here rather than the panel's, so `onResize` is a
 *  no-op - the AoA page already owns a drag handle for the whole side panel.
 */
const InspectorArtifactView: React.FC = () => {
  const { artifacts } = useInspector();
  // Defence in depth, matching how regular chat gates the same panel. The
  // backend is the real gate — with file authoring off the tools are never
  // registered, so no artifact event can fire — but an unguarded render here
  // would show a panel for a capability the tenant does not have if that ever
  // regressed. Either flag is enough: an AoA artifact rides file_authoring,
  // and agent_outputs covers one written by a regular agent.
  const filesEnabled = useFeature('modules.agent_of_agents.file_authoring');
  const outputsEnabled = useFeature('modules.agent_outputs');
  if (!filesEnabled && !outputsEnabled) return null;
  return (
    <ArtifactErrorBoundary>
      <ArtifactPanel
        artifacts={artifacts.artifacts}
        order={artifacts.order}
        activeId={artifacts.activeId}
        onSelect={artifacts.setActiveId}
        onViewVersion={artifacts.viewVersion}
        onResize={() => { /* the inspector owns the width */ }}
        onDeleteVersion={artifacts.deleteVersion}
      />
    </ArtifactErrorBoundary>
  );
};
